// Relative `.ts` imports only: `send-filter-alerts` (Deno) imports this codec (denoCompat guard).
import type { SearchArea, SourcePostFilters } from './serpRead/types.ts';
import { normalizujMiejsca } from './serpRead/miejsce.ts';
import type { SavedFilterKey, SavedFiltersDoc } from '../types/savedSearch.ts';

/**
 * CODEC: `SourcePostFilters` (the results page) ⇄ `SavedFiltersDoc` (the stored document).
 *
 * The page keeps Polish keys and values (`zeZwierzeciem`, `umeblowanie: 'umeblowane'`); the
 * database is English-only (Convention 1), so the document renames them. Pure, no React, no
 * Supabase client — the future `send-filter-alerts` edge function imports it too (F3/C8).
 *
 * What is NOT part of the saved set and is dropped on the way in: the debug lenses
 * (`includeDead`, `noPrice`, `approxLocation`, `sanityOutliers`), `view` (list/map is a
 * rendering choice) and „Ulubione” (`ulubione`, `ulubioneIds` — plan §1.14). Legacy `suburbs` are folded into `areas` as districts.
 */

/** `SourcePostFilters` keys that never enter the document. */
export const UNSAVED_FILTER_KEYS = [
  'includeDead', 'noPrice', 'approxLocation', 'sanityOutliers', 'view', 'suburbs',
  // „Ulubione” (A6, §1.14): a list filter, not a search criterion — never saved, never e-mailed.
  'ulubione', 'ulubioneIds',
] as const;
type UnsavedFilterKey = (typeof UNSAVED_FILTER_KEYS)[number];

/** Exhaustive: a new `SourcePostFilters` key fails typecheck here until it is mapped or excluded. */
const KEY_MAP: Record<Exclude<keyof SourcePostFilters, UnsavedFilterKey>, SavedFilterKey> = {
  postingTypes: 'posting_types',
  areas: 'areas',
  rentMin: 'rent_min',
  rentMax: 'rent_max',
  costMode: 'cost_mode',
  rooms: 'rooms',
  areaMin: 'area_min',
  areaMax: 'area_max',
  nearMetro: 'near_metro',
  poi: 'poi',
  postedSinceHours: 'posted_since_hours',
  zeZwierzeciem: 'pet_friendly',
  umeblowanie: 'furnishing',
  pietro: 'floor',
  kuchnia: 'kitchen',
  lazienki: 'bathrooms',
  balkon: 'balcony',
  winda: 'elevator',
  kaucjaMax: 'deposit_max',
  udogodnienia: 'amenities',
  internet: 'internet',
  garaz: 'underground_garage',
  nieznaneOplaty: 'unknown_fees',
  bezZdjec: 'show_without_photos',
  bezAdresu: 'without_address',
  availableNow: 'available_now',
  typBudynku: 'building_type',
  wiekBudynku: 'building_age',
  wlascicielNaFlatHero: 'owner_on_flathero',
  sort: 'sort',
  polygon: 'polygon',
};

const FURNISHING_TO_DOC = { umeblowane: 'furnished', nieumeblowane: 'unfurnished' } as const;
const KITCHEN_TO_DOC = { oddzielna: 'separate', aneks: 'combined' } as const;

function invert<K extends string, V extends string>(m: Record<K, V>): Record<V, K> {
  return Object.fromEntries(Object.entries(m).map(([k, v]) => [v, k])) as Record<V, K>;
}
const FURNISHING_FROM_DOC = invert(FURNISHING_TO_DOC);
const KITCHEN_FROM_DOC = invert(KITCHEN_TO_DOC);
const KEY_MAP_BACK = invert(KEY_MAP) as Record<SavedFilterKey, Exclude<keyof SourcePostFilters, UnsavedFilterKey>>;

const isEmpty = (v: unknown): boolean =>
  v === undefined || v === null || (Array.isArray(v) && v.length === 0);

/** Results-page filters → the document to store. Unset and empty values are omitted. */
export function toSavedFiltersDoc(f: SourcePostFilters): SavedFiltersDoc {
  const doc: Record<string, unknown> = {};

  const areas: SearchArea[] = [...(f.areas ?? [])];
  for (const s of f.suburbs ?? []) {
    if (!areas.some((a) => a.kind === 'district' && a.name === s)) areas.push({ kind: 'district', name: s });
  }
  // One „Blisko do…” place at a time (plan §1.18): the server rejects a second one.
  const areasOk = normalizujMiejsca(areas);
  if (areasOk.length) doc.areas = areasOk;

  for (const [from, to] of Object.entries(KEY_MAP) as Array<[keyof SourcePostFilters, SavedFilterKey]>) {
    if (from === 'areas') continue;
    const v = f[from];
    if (isEmpty(v)) continue;
    if (from === 'umeblowanie') doc[to] = FURNISHING_TO_DOC[v as keyof typeof FURNISHING_TO_DOC];
    else if (from === 'kuchnia') doc[to] = KITCHEN_TO_DOC[v as keyof typeof KITCHEN_TO_DOC];
    else if (from === 'pietro') doc[to] = 'ground';
    else if (from === 'internet') doc[to] = 'fibre_only';
    else if (from === 'rooms') doc[to] = [...new Set(v as number[])].sort((a, b) => a - b);
    else doc[to] = v;
  }
  return doc as SavedFiltersDoc;
}

/** Stored document → results-page filters. Keys this client does not know are ignored. */
export function fromSavedFiltersDoc(doc: SavedFiltersDoc | null | undefined): SourcePostFilters {
  const f: Record<string, unknown> = {};
  if (!doc) return f;
  for (const [key, v] of Object.entries(doc) as Array<[SavedFilterKey, unknown]>) {
    const to = KEY_MAP_BACK[key];
    if (!to || isEmpty(v)) continue;
    if (key === 'furnishing') f[to] = FURNISHING_FROM_DOC[v as keyof typeof FURNISHING_FROM_DOC];
    else if (key === 'kitchen') f[to] = KITCHEN_FROM_DOC[v as keyof typeof KITCHEN_FROM_DOC];
    else if (key === 'floor') f[to] = 'parter';
    else if (key === 'internet') f[to] = 'swiatlowod';
    else if (key === 'areas') {
      const areas = normalizujMiejsca(v as SearchArea[]);
      if (areas.length) f[to] = areas;
    } else f[to] = v;
  }
  return f as SourcePostFilters;
}
